/**
 * Capacitor native-shell detection.
 *
 * The TurnKey Coach native apps are Capacitor shells that load the live web app
 * (https://app.turnkey.coach) in a webview, replacing the MobiLoud Canvas wrapper.
 * When running inside that shell, Capacitor injects `window.Capacitor` with a
 * native bridge; `isNativePlatform()` returns true on device (false in a desktop
 * browser or when the site is opened normally on mobile web).
 *
 * Detection is done via the injected global (not a bundled import) so this module
 * is safe to load in every environment, including plain mobile web and SSR.
 */

/**
 * @returns {boolean} true when running inside the Capacitor native shell (iOS/Android)
 */
export function isCapacitorEnvironment() {
  return (
    typeof window !== "undefined" &&
    typeof window.Capacitor !== "undefined" &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform() === true
  );
}

/**
 * @returns {"ios"|"android"|null} the native platform, or null on web
 */
export function capacitorPlatform() {
  if (!isCapacitorEnvironment()) {
    return null;
  }
  try {
    return window.Capacitor.getPlatform();
  } catch {
    return null;
  }
}

/**
 * Whether a named Capacitor plugin is registered in the current native shell.
 * Returns false outside the Capacitor environment or if the lookup throws.
 * Use this before calling any plugin that may not be installed on all platforms.
 *
 * @param {string} pluginName - the plugin class name, e.g. "Network", "Badge"
 * @returns {boolean}
 */
export function isCapacitorPluginAvailable(pluginName) {
  if (!isCapacitorEnvironment()) {
    return false;
  }
  try {
    return (
      typeof window.Capacitor.isPluginAvailable === "function" &&
      window.Capacitor.isPluginAvailable(pluginName) === true
    );
  } catch {
    return false;
  }
}
